import { format } from "date-fns";
import { capitalize } from "lodash";

import {
  GoalBasedPlanDateResponse,
  GoalBasedPlanDateTypeSchema,
  GoalBasedRepeatTimeFrame,
  GoalBasedRepeatTimeFrameSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { repeatTimeFrameOptions } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import { DateRangeRepeatsTextStrings } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Types";

import { convertAndFormatAgeToDateString } from "./convertAgeToDate";

const unknown = "unknown";
interface DateRangeStringProps {
  startDate: GoalBasedPlanDateResponse | undefined;
  endDate: GoalBasedPlanDateResponse | undefined;
  clientAndSpouseDetails: ClientAndSpouseDetails | undefined;
  repeats?: GoalBasedRepeatTimeFrame;
  repeatsTimeFrameText?: DateRangeRepeatsTextStrings;
  owner: ClientId;
}

const getRepeatsOptionDisplayText = (
  repeats: GoalBasedRepeatTimeFrame | undefined,
  repeatsTimeFrameText?: DateRangeStringProps["repeatsTimeFrameText"]
): string => {
  const { None, EveryMonth, EveryYear } = GoalBasedRepeatTimeFrameSchema.Enum;

  const { From } = DateRangeRepeatsTextStrings;

  if (repeats === undefined || repeats === None) {
    return capitalize(From);
  }

  const repeatsOption = repeatTimeFrameOptions.find(
    (el) => el.value === repeats
  );

  if (repeatsOption === undefined) {
    return capitalize(From);
  }

  const defaultRepeatsText =
    repeatsTimeFrameText === undefined ? From : repeatsTimeFrameText;

  const lowerCaseYear = repeatsOption.label.replace(
    new RegExp(`\\b${"Years"}\\b`, "gi"),
    "years"
  );

  const repeatsMoreThanOneYear = [None, EveryMonth, EveryYear].some(
    (d) => d !== repeatsOption.value
  );

  const repeatsLabel = repeatsMoreThanOneYear
    ? lowerCaseYear
    : repeatsOption.label;

  switch (repeatsOption.value) {
    case None:
      return "";
    case EveryMonth:
      return `Monthly ${defaultRepeatsText}`;
    case EveryYear:
      return `Annually ${defaultRepeatsText}`;
    default:
      return `${repeatsLabel} ${defaultRepeatsText}`;
  }
};

export const getDateRangeStringFromPlanDates = ({
  startDate,
  endDate,
  clientAndSpouseDetails,
  repeats,
  owner,
}: DateRangeStringProps): string => {
  const { Age, DateString, AlreadyStarted } = GoalBasedPlanDateTypeSchema.Enum;

  const clientDob = clientAndSpouseDetails?.client.dob;

  const spouseDob = clientAndSpouseDetails?.spouse?.dob;

  const dateOfBirth =
    owner === clientAndSpouseDetails?.client.clientId ? clientDob : spouseDob;

  if (clientAndSpouseDetails === undefined) {
    return "";
  }

  if (startDate === undefined) {
    return "";
  }

  const dateStringPrefix = getRepeatsOptionDisplayText(repeats);

  if (startDate.dateType === AlreadyStarted) {
    return `${dateStringPrefix} ${new Date().getFullYear()}`;
  }

  if (
    endDate === undefined ||
    repeats === GoalBasedRepeatTimeFrameSchema.Enum.None
  ) {
    if (startDate.dateType === DateString) {
      const date = format(new Date(startDate.value.replace(/-/g, "/")), "yyyy");
      return date ?? unknown;
    }

    if (startDate.dateType === Age && dateOfBirth !== undefined) {
      return convertAndFormatAgeToDateString({
        value: startDate.value,
        dateOfBirth,
        formatString: "yyyy",
      });
    }
  }

  let start = startDate.value;

  if (startDate.dateType === Age && dateOfBirth !== undefined) {
    start = convertAndFormatAgeToDateString({
      value: startDate.value,
      dateOfBirth,
      formatString: "yyyy",
    });
  } else if (startDate.dateType === DateString) {
    start = format(new Date(startDate.value.replace(/-/g, "/")), "yyyy");
  }

  if (endDate === undefined) {
    return `${dateStringPrefix} ${start}`;
  }

  let end = endDate.value;

  if (endDate.dateType === Age && dateOfBirth !== undefined) {
    end = convertAndFormatAgeToDateString({
      value: endDate.value,
      dateOfBirth,
      formatString: "yyyy",
    });
  } else if (endDate.dateType === DateString) {
    end = format(new Date(endDate.value.replace(/-/g, "/")), "yyyy");
  }

  return `${dateStringPrefix} ${start} to ${end}`;
};
